import { useControllableState } from '@doppio/core';
import { Text, useDripsyTheme, useSx } from 'dripsy';
import * as React from 'react';
import { Animated, Pressable } from 'react-native';
const TEST_ID = 'doppio-switch';
const spring = (property, toValue) => Animated.spring(property, {
    toValue,
    speed: 50,
    bounciness: 0,
    useNativeDriver: false,
});
const Switch = React.forwardRef(({ accessibilityLabel, children, defaultChecked = false, isDisabled = false, id, onCheckedChange, isRequired = false, size, ...props }, forwardedRef) => {
    const sx = useSx();
    const { theme } = useDripsyTheme();
    const [checked, setChecked] = useControllableState({
        value: props.isChecked,
        defaultValue: defaultChecked,
        onChange: onCheckedChange,
    });
    const shouldRenderHiddenHTMLFormControl = 'name' in props && Boolean(props.name);
    const shouldRenderLabel = Boolean(children);
    if (shouldRenderHiddenHTMLFormControl)
        console.warn(`[doppio] elements/Switch: prop "name" was set as "${props.name}"; assuming platform target to be "web"`);
    const translateX = React.useRef(new Animated.Value(0)).current;
    const trackColor = React.useRef(new Animated.Value(0)).current;
    const mapSizeToTrackHeight = {
        big: theme.sizes.$7,
    };
    const mapSizeToTrackWidth = {
        big: theme.sizes.$10,
    };
    const switchTrackHeight = (size && mapSizeToTrackHeight[size]) ?? theme.sizes.$6;
    const switchTrackWidth = (size && mapSizeToTrackWidth[size]) ?? theme.sizes.$8;
    const switchThumbSize = switchTrackHeight;
    const animations = React.useCallback(() => {
        Animated.parallel([
            spring(translateX, checked ? switchTrackWidth - switchThumbSize : 0),
            spring(trackColor, checked ? 1 : 0),
        ]).start();
    }, [checked, translateX, switchTrackWidth, switchThumbSize, trackColor]);
    React.useEffect(() => {
        animations();
    }, [animations]);
    const offColor = theme.colors[theme.forms.switch.$$off.backgroundColor] ||
        theme.forms.switch.$$off.backgroundColor;
    const onColor = theme.colors[theme.forms.switch.$$on.backgroundColor] ||
        theme.forms.switch.$$on.backgroundColor;
    const trackColorInterpolation = trackColor.interpolate({
        inputRange: [0, 1],
        outputRange: [offColor, onColor],
    });
    return (React.createElement(Pressable, { style: {
            flexDirection: 'row',
        }, accessibilityRequired: isRequired, accessibilityLabel: accessibilityLabel, accessibilityRole: "switch", accessibilityState: {
            checked,
            disabled: isDisabled,
        }, ref: forwardedRef, nativeID: id, testID: TEST_ID, disabled: isDisabled, onPress: () => setChecked(!checked) },
        shouldRenderHiddenHTMLFormControl &&
            React.createElement('input', {
                type: 'checkbox',
                name: props.name,
                value: checked,
                'aria-hidden': true,
                'data-testid': `${TEST_ID}-hidden-input`,
                style: {
                    border: 0,
                    clip: 'rect(0 0 0 0)',
                    height: 1,
                    margin: -1,
                    overflow: 'hidden',
                    padding: 0,
                    position: 'absolute',
                    width: 1,
                    whiteSpace: 'nowrap',
                    wordWrap: 'normal',
                },
            }),
        React.createElement(Animated.View, { style: [
                sx({
                    alignItems: 'center',
                    borderRadius: 'full',
                    display: 'flex',
                    flexDirection: 'row',
                    width: switchTrackWidth,
                    height: switchTrackHeight,
                    ...(isDisabled && {
                        backgroundColor: 'secondaryBackgroundDisabled',
                        cursor: 'not-allowed',
                    }),
                }),
                !isDisabled && { backgroundColor: trackColorInterpolation },
            ] },
            React.createElement(Animated.View, { style: [
                    sx({
                        backgroundColor: 'background',
                        borderWidth: 1.5,
                        borderStyle: 'solid',
                        borderRadius: 'full',
                        height: switchThumbSize,
                        width: switchThumbSize,
                        ...(isDisabled && {
                            borderColor: 'secondaryBackgroundDisabled',
                        }),
                    }),
                    { transform: [{ translateX }] },
                    !isDisabled && {
                        borderColor: trackColorInterpolation,
                    },
                ] })),
        shouldRenderLabel && (React.createElement(Text, { testID: `${TEST_ID}-label`, sx: {
                paddingLeft: '$3',
                fontFamily: 'root',
                fontSize: '$2',
                fontWeight: 'normal',
                lineHeight: size === 'big' ? '$5' : '$4',
                ...(isDisabled && {
                    cursor: 'not-allowed',
                }),
            } }, children))));
});
const SwitchCompound = Object.assign({}, Switch, { displayName: 'Switch', testID: TEST_ID });
export { SwitchCompound as Switch };
//# sourceMappingURL=Switch.js.map